import React, { useEffect, useMemo, useState } from 'react';
import settingsService from '../services/settingsService';
import './Settings.css';

// The settings registry is generic: the API says what each setting is, and
// this page renders the matching control. Adding a setting on the backend
// makes it appear here with no change to this file.
const Settings = () => {
  const [settings, setSettings] = useState([]);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const applySettings = (loaded) => {
    setSettings(loaded);
    setValues(
      loaded.reduce((acc, setting) => {
        acc[setting.key] = setting.value ?? (setting.type === 'boolean' ? false : '');
        return acc;
      }, {})
    );
  };

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        applySettings(await settingsService.getSettings());
      } catch (err) {
        console.error('Failed to load settings', err);
        setError('Failed to load settings. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  // Settings arrive already ordered by group, so grouping preserves that order.
  const groups = useMemo(() => {
    const byGroup = new Map();

    settings.forEach((setting) => {
      if (!byGroup.has(setting.group_name)) {
        byGroup.set(setting.group_name, []);
      }
      byGroup.get(setting.group_name).push(setting);
    });

    return [...byGroup.entries()];
  }, [settings]);

  const handleChange = (key, value) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setSuccess('');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      setSaving(true);
      applySettings(await settingsService.updateSettings(values));
      setSuccess('Settings saved.');
    } catch (err) {
      console.error('Failed to save settings', err);
      setError(
        err?.response?.data?.message ||
          'Failed to save settings. Please try again.'
      );
    } finally {
      setSaving(false);
    }
  };

  const renderControl = (setting) => {
    const value = values[setting.key];

    if (setting.type === 'boolean') {
      return (
        <button
          type="button"
          role="switch"
          aria-checked={!!value}
          aria-labelledby={`${setting.key}-label`}
          className={`toggle${value ? ' toggle-on' : ''}`}
          onClick={() => handleChange(setting.key, !value)}
        >
          <span className="toggle-knob" />
        </button>
      );
    }

    return (
      <input
        id={setting.key}
        type={setting.type === 'number' ? 'number' : 'text'}
        value={value ?? ''}
        onChange={(e) => handleChange(setting.key, e.target.value)}
      />
    );
  };

  if (loading) {
    return (
      <div className="settings-page">
        <h1 className="page-title">Settings</h1>
        <p>Loading settings...</p>
      </div>
    );
  }

  return (
    <div className="settings-page">
      <h1 className="page-title">Settings</h1>
      <p className="page-subtitle">
        Shop-wide switches and values. Changes apply to the storefront as soon
        as they are saved.
      </p>

      {error && <div className="admin-alert admin-alert-error">{error}</div>}
      {success && (
        <div className="admin-alert admin-alert-success">{success}</div>
      )}

      {settings.length === 0 ? (
        <div className="card settings-empty">
          <p>No settings are available yet.</p>
        </div>
      ) : (
        <form onSubmit={handleSave} className="settings-form">
          {groups.map(([groupName, groupSettings]) => (
            <section className="card" key={groupName}>
              <h2>{groupName}</h2>

              <ul className="settings-list">
                {groupSettings.map((setting) => (
                  <li
                    className={`setting-row setting-row-${setting.type}`}
                    key={setting.key}
                  >
                    <div className="setting-copy">
                      <label id={`${setting.key}-label`} htmlFor={setting.key}>
                        {setting.label}
                      </label>
                      {setting.description && (
                        <p className="hint">{setting.description}</p>
                      )}
                    </div>

                    <div className="setting-control">
                      {renderControl(setting)}
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          ))}

          <div className="settings-actions">
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save settings'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default Settings;
